import { useQuery } from '@apollo/client';
import {
  Activity,
  Bone,
  CalendarClock,
  Clock3,
  Heart,
  Info,
  Monitor,
  PawPrint,
  ShieldCheck,
  Stethoscope,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MEDICAL_QUEUE_BOARD_QUERY,
  REGISTERED_ARRIVALS_BOARD_QUERY,
  WAITING_ROOM_BOARD_QUERY,
  WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
} from './display-boards.gql';

import cat_1_image from '@/assets/pets/cat_1.webp';
import cat_2_image from '@/assets/pets/cat_2.webp';
import cat_3_image from '@/assets/pets/cat_3.webp';
import dog_1_image from '@/assets/pets/dog_1.webp';
import dog_2_image from '@/assets/pets/dog_2.webp';
import dog_3_image from '@/assets/pets/dog_3.webp';
import no_cat_no_dog_image from '@/assets/pets/no_cat_no_dog.webp';
import { TotemMarkOutline, TotemWordmark } from '@/shared/brand/TotemLogo';
import { cn } from '@/shared/lib/cn';
import { YoutubePlaylistPlayer } from '@/shared/ui/YoutubePlaylistPlayer';

interface WaitingRoomCard {
  consulting_room_id: string;
  consulting_room_name: string;
  display_order: number;
  zone_type: string;
  occupancy_status: 'free' | 'occupied';
  veterinarian_name: string | null;
  pet_name: string | null;
  pet_species: string | null;
  reason_label: string | null;
}

interface WaitingRoomResponse {
  waitingRoomBoard: WaitingRoomCard[];
}

interface MedicalQueueCard {
  sale_order_id: string;
  pet_name: string | null;
  pet_species: string | null;
  consulting_room_name: string | null;
  queued_at: string;
}

interface MedicalQueueResponse {
  medicalQueueBoard: MedicalQueueCard[];
}

interface WaitingRoomDisplaySettingsResponse {
  waitingRoomDisplaySettings: {
    youtube_playlist_id: string | null;
    is_configured: boolean;
  };
}

interface RegisteredArrivalCard {
  appointment_id: string;
  folio_number: string;
  pet_name: string | null;
  pet_species: string | null;
  veterinarian_name: string | null;
  chief_complaint: string | null;
  checked_in_at: string;
}

interface RegisteredArrivalsResponse {
  registeredArrivalsBoard: RegisteredArrivalCard[];
}

const DOG_IMAGES = [dog_1_image, dog_2_image, dog_3_image] as const;
const CAT_IMAGES = [cat_1_image, cat_2_image, cat_3_image] as const;
const FALLBACK_WAITING_ROOM_YOUTUBE_PLAYLIST_ID =
  import.meta.env.VITE_WAITING_ROOM_YOUTUBE_PLAYLIST_ID?.trim() ?? '';

/**
 * 3-column kiosk split — stacked below `lg` (phones/tablets), a narrower fixed split from
 * `lg`-`2xl` (laptop-size windows), and the exact pixel design once the screen is wide enough
 * to fit it (`2xl`, 1536px+). Discrete steps (rather than a continuously fluid `vw`/`clamp`
 * split) keep child element sizing predictable — every column width children are tuned
 * against is one of exactly three known values, never an in-between one.
 */
const COL_TEMPLATE =
  'grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)_230px] 2xl:grid-cols-[556px_minmax(0,1fr)_320px]';

type PetSpeciesLabel = 'Canino' | 'Felino' | 'Otros';

function formatPetSpecies(value: string | null | undefined): PetSpeciesLabel {
  const token = value?.trim().toLowerCase() ?? '';
  if (
    token === 'canino' ||
    token === 'canina' ||
    token === 'perro' ||
    token === 'perra' ||
    token === 'dog' ||
    token === 'dogs' ||
    token === 'canine'
  ) {
    return 'Canino';
  }
  if (
    token === 'felino' ||
    token === 'felina' ||
    token === 'gato' ||
    token === 'gata' ||
    token === 'cat' ||
    token === 'cats' ||
    token === 'feline'
  ) {
    return 'Felino';
  }
  return 'Otros';
}

/** Small stable hash so the same patient always gets the same stock photo across polls. */
function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function pickStable<T>(items: readonly T[], seed: string): T {
  return items[hashSeed(seed) % items.length] as T;
}

function getPetImage(species: PetSpeciesLabel, seed: string): string {
  if (species === 'Canino') return pickStable(DOG_IMAGES, seed);
  if (species === 'Felino') return pickStable(CAT_IMAGES, seed);
  return no_cat_no_dog_image;
}

function normalizeZoneType(value: string): 'consulting_room' | 'xray' | 'ultrasound' | 'other' {
  switch (value.toLowerCase()) {
    case 'xray':
      return 'xray';
    case 'ultrasound':
      return 'ultrasound';
    case 'other':
      return 'other';
    default:
      return 'consulting_room';
  }
}

function formatQueuedTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return 'No disponible';
  }
}

function LiveClock(): JSX.Element {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1_000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const hour_text = now.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const date_text = now.toLocaleDateString('es-MX', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="flex h-auto items-center gap-2 rounded-[10px] border border-border bg-card px-3 py-2 shadow-sm sm:gap-4 sm:px-6 lg:h-[86px]">
      <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground sm:h-11 sm:w-11">
        <Clock3 className="h-4 w-4 sm:h-6 sm:w-6" aria-hidden />
      </div>
      <div>
        <p className="font-display text-[20px] font-black tabular-nums leading-none text-foreground sm:text-[28px] lg:text-[34px]">
          {hour_text}
        </p>
        <p className="mt-1 text-[10px] font-bold capitalize text-muted-foreground sm:text-[13px]">
          {date_text}
        </p>
      </div>
    </div>
  );
}

/** Shared meta rows for a turn card (species / breed / official intake time / assigned vet). */
function TurnMeta({
  species,
  queued_at,
  variant,
}: {
  species: PetSpeciesLabel;
  queued_at: string;
  variant: 'hero' | 'compact';
}): JSX.Element {
  const text_class = variant === 'hero' ? 'text-primary-foreground' : 'text-primary-foreground/95';
  const icon_class = variant === 'hero' ? 'text-primary-foreground/80' : 'text-primary-foreground/70';

  return (
    <div className={cn('space-y-1 text-[11px] font-bold leading-none sm:space-y-1.5 sm:text-[13px]', text_class)}>
      <p className="flex items-center gap-2">
        <PawPrint className={cn('h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4', icon_class)} aria-hidden />
        Especie: {species}
      </p>
      <p className="flex items-center gap-2">
        <Bone className={cn('h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4', icon_class)} aria-hidden />
        Raza: No registrada
      </p>
      <p className="flex items-center gap-2">
        <CalendarClock className={cn('h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4', icon_class)} aria-hidden />
        Ingreso oficial: {formatQueuedTime(queued_at)}
      </p>
      <p className="flex items-center gap-2">
        <UserRound className={cn('h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4', icon_class)} aria-hidden />
        MVZ: Sin asignar
      </p>
    </div>
  );
}

function RoomTypeIcon({ zone_type }: { zone_type: string }): JSX.Element {
  const normalized = normalizeZoneType(zone_type);
  if (normalized === 'consulting_room') {
    return (
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground sm:h-12 sm:w-12 lg:h-14 lg:w-14">
        <Stethoscope className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" aria-hidden />
      </div>
    );
  }
  return (
    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-foreground text-background sm:h-12 sm:w-12 lg:h-14 lg:w-14">
      <Monitor className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" aria-hidden />
    </div>
  );
}

export function WaitingRoomBoardPage(): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<WaitingRoomResponse>(WAITING_ROOM_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5_000,
  });
  const { data: queue_data } = useQuery<MedicalQueueResponse>(MEDICAL_QUEUE_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5_000,
  });
  const { data: display_settings_data } = useQuery<WaitingRoomDisplaySettingsResponse>(
    WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 60_000,
    },
  );
  const { data: registered_data } = useQuery<RegisteredArrivalsResponse>(
    REGISTERED_ARRIVALS_BOARD_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 5_000,
    },
  );

  const rooms = data?.waitingRoomBoard ?? [];
  const in_progress_rooms = rooms.filter((room) => room.occupancy_status === 'occupied');
  const waiting_cards = queue_data?.medicalQueueBoard ?? [];
  const now_serving = waiting_cards[0] ?? null;
  const next_turns = waiting_cards.slice(1, 4);
  const registered_arrivals = registered_data?.registeredArrivalsBoard ?? [];
  const display_settings = display_settings_data?.waitingRoomDisplaySettings;
  const youtube_playlist_id =
    display_settings?.is_configured === true
      ? (display_settings.youtube_playlist_id?.trim() ?? '')
      : FALLBACK_WAITING_ROOM_YOUTUBE_PLAYLIST_ID;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-muted p-1 text-foreground sm:p-2">
      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-8px)] max-w-[1920px] flex-col gap-2 rounded-[12px] bg-card p-2 shadow-[0_0_24px_rgba(13,38,84,0.18)] sm:gap-3 sm:p-3">
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-border bg-card px-4 py-3 shadow-[0_6px_18px_rgba(19,39,80,0.10)] sm:px-6 lg:h-[104px] lg:flex-nowrap lg:px-8 lg:py-0">
          <div className="flex items-center gap-3">
            <TotemMarkOutline className="h-10 w-auto sm:h-12 lg:h-14" title="Totem" />
            <div>
              <TotemWordmark className="h-5 w-auto text-primary sm:h-6" title="tótem" />
              <p className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground sm:text-[10px]">
                Historial clínico de mascotas
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground sm:h-11 sm:w-11">
              <Clock3 className="h-4 w-4 sm:h-6 sm:w-6" aria-hidden />
            </div>
            <div>
              <h1 className="font-display text-[16px] font-black uppercase leading-none text-foreground sm:text-[20px] lg:text-[27px]">
                Sala de espera
              </h1>
              <p className="mt-1 text-[11px] font-bold text-muted-foreground sm:text-[13px]">
                Monitoreo de Atención en Vivo
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:gap-4">
            <LiveClock />
            <div className="hidden h-[86px] w-[110px] shrink-0 flex-col items-center justify-center gap-1 rounded-[10px] bg-primary text-primary-foreground shadow-[0_8px_16px_rgba(25,77,157,0.32)] sm:flex">
              <p className="font-display text-[12px] font-black uppercase leading-none">Pantalla</p>
              <Monitor className="h-6 w-6" aria-hidden />
              <p className="font-display text-[26px] font-black leading-none">01</p>
            </div>
          </div>
        </header>

        {registered_arrivals.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-accent/40 bg-accent/5 px-4 py-2 sm:px-6">
            <span className="shrink-0 rounded-full bg-accent px-3 py-1 text-[11px] font-black uppercase leading-none text-accent-foreground sm:text-[12px]">
              Registrados en recepción
            </span>
            {registered_arrivals.map((arrival) => (
              <span
                key={arrival.appointment_id}
                className="rounded-full border border-border bg-card px-3 py-1 text-[12px] font-bold text-foreground sm:text-[13px]"
              >
                {arrival.pet_name ?? t('display.waiting_room.no_pet')}
                <span className="mx-1.5 text-border">·</span>
                {formatQueuedTime(arrival.checked_in_at)}
              </span>
            ))}
          </div>
        ) : null}

        <main className="flex flex-1 flex-col gap-2 sm:gap-3">
          <div className={cn('grid flex-1 gap-2 sm:gap-3', COL_TEMPLATE)}>
            {/* Column 1 — now serving + info strip */}
            <div className="flex flex-col gap-2 sm:gap-3">
              <NowServingCard card={now_serving} t={t} />
              <div className="flex min-h-[52px] items-center gap-3 rounded-[9px] border border-border bg-muted px-3 py-2 text-[12px] font-bold text-muted-foreground sm:px-5 sm:text-[13px] 2xl:h-[62px]">
                <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-accent text-accent-foreground sm:h-8 sm:w-8">
                  <Info className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                </div>
                Las consultas de especialidad las toma la Médico especialista asignada al
                consultorio correspondiente.
                <PawPrint className="ml-auto hidden h-7 w-7 shrink-0 text-border sm:block" aria-hidden />
              </div>
            </div>

            {/* Column 2 — attention in progress */}
            <section className="flex min-h-[220px] flex-col overflow-hidden rounded-[13px] border border-border bg-card p-3 shadow-[0_8px_18px_rgba(19,39,80,0.10)] sm:p-4 lg:p-6">
              <div className="mb-4 flex items-center gap-3 border-b-2 border-accent/70 pb-3">
                <Activity className="h-6 w-6 text-accent sm:h-8 sm:w-8" aria-hidden />
                <h2 className="font-display text-[16px] font-black uppercase leading-none text-foreground sm:text-[20px] lg:text-[22px]">
                  Atención en curso
                </h2>
              </div>

              {in_progress_rooms.length === 0 ? (
                <div className="flex flex-1 items-center justify-center py-8">
                  <p className="text-center font-display text-[16px] font-black uppercase text-success sm:text-[20px]">
                    No hay consultas en curso.
                  </p>
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto">
                  {in_progress_rooms.map((room) => {
                    const normalized = normalizeZoneType(room.zone_type);
                    const in_process = normalized !== 'consulting_room';
                    const species = formatPetSpecies(room.pet_species);
                    const pet_image = getPetImage(species, room.consulting_room_id);
                    return (
                      <article
                        key={room.consulting_room_id}
                        className="flex items-center gap-3 rounded-[10px] border border-border bg-card px-3 py-3 shadow-[0_4px_12px_rgba(19,39,80,0.06)] sm:gap-4 sm:px-5 sm:py-4"
                      >
                        <RoomTypeIcon zone_type={room.zone_type} />
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            <h3 className="font-display text-[15px] font-black leading-none text-foreground sm:text-[18px]">
                              {room.consulting_room_name}
                            </h3>
                            <span
                              className={cn(
                                'rounded-full px-3 py-1 text-[11px] font-black uppercase leading-none text-white',
                                in_process ? 'bg-warning' : 'bg-success',
                              )}
                            >
                              {in_process ? 'En proceso' : 'En consulta'}
                            </span>
                          </div>
                          <p className="mt-2 truncate text-[12px] font-bold text-muted-foreground sm:text-[13px]">
                            <span className="text-foreground">Médico a cargo:</span>{' '}
                            {room.veterinarian_name ?? 'Por asignar'}
                            <span className="mx-2 text-border">|</span>
                            <span className="text-foreground">Paciente:</span>{' '}
                            {room.pet_name ?? t('display.waiting_room.no_pet')}
                            <span className="mx-2 text-border">|</span>
                            <span className="text-foreground">Especie:</span> {species}
                            <span className="mx-2 text-border">|</span>
                            <span className="text-foreground">Motivo:</span>{' '}
                            {room.reason_label ?? 'Consulta general'}
                          </p>
                        </div>
                        <img
                          src={pet_image}
                          alt={species}
                          className="h-12 w-12 shrink-0 rounded-full border border-border object-cover sm:h-14 sm:w-14 lg:h-16 lg:w-16"
                        />
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Column 3 — health campaign + live video */}
            <div className="flex flex-col gap-2 sm:gap-3">
              <article className="relative h-auto min-h-[110px] overflow-hidden rounded-[13px] border border-border bg-accent/10 p-3 sm:min-h-[190px] sm:p-4 2xl:h-[210px] 2xl:p-5">
                {/* The pet photos only appear once the card is wide enough (2xl, the true kiosk
                    tier) to fit them beside the text without the text running underneath them. */}
                <div className="2xl:max-w-[70%]">
                  <div className="flex items-center gap-2 text-accent">
                    <Heart className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                    <p className="text-[11px] font-black uppercase tracking-wide sm:text-[12px]">
                      Campaña de salud
                    </p>
                  </div>
                  <h3 className="mt-2 font-display text-[18px] font-black leading-tight text-primary sm:text-[22px] 2xl:text-[24px]">
                    Chequeo Geriátrico
                  </h3>
                  <p className="mt-1 text-[11px] font-bold leading-snug text-muted-foreground sm:text-[13px]">
                    Pregunta los detalles con recepción.
                  </p>
                </div>
                <Heart
                  className="absolute right-8 top-6 hidden h-6 w-6 rotate-12 text-accent/40 2xl:block"
                  aria-hidden
                />
                <Heart
                  className="absolute right-16 top-14 hidden h-4 w-4 -rotate-12 text-accent/30 2xl:block"
                  aria-hidden
                />
                <div className="absolute bottom-0 right-0 hidden items-end 2xl:flex">
                  <img
                    src={cat_2_image}
                    alt=""
                    className="h-[92px] w-[92px] rounded-tl-[13px] object-cover"
                  />
                  <img
                    src={dog_1_image}
                    alt=""
                    className="h-[110px] w-[100px] rounded-tl-[13px] object-cover"
                  />
                </div>
              </article>

              <article className="relative h-[240px] overflow-hidden rounded-[13px] border border-border bg-primary shadow-[0_4px_12px_rgba(19,39,80,0.08)] sm:h-[300px] lg:h-auto lg:flex-1">
                <YoutubePlaylistPlayer
                  playlistId={youtube_playlist_id}
                  autoplay
                  withSound
                  showControls
                  className="h-full w-full rounded-[13px] border-0 shadow-none"
                  frameClassName="h-full w-full"
                  title="Playlist para sala de espera Totem"
                />
                <div className="pointer-events-none absolute left-2 top-2 flex items-center gap-2 rounded-[8px] bg-primary/90 px-2 py-1.5 text-primary-foreground shadow-[0_6px_14px_rgba(0,0,0,0.18)] sm:left-4 sm:top-4 sm:px-3 sm:py-2">
                  <span className="totem-live-pulse h-2.5 w-2.5 shrink-0 rounded-full bg-success sm:h-3 sm:w-3" />
                  <div>
                    <p className="font-display text-[10px] font-black uppercase leading-none sm:text-[12px]">
                      Activo en tiempo real
                    </p>
                    <p className="mt-1 hidden text-[10px] font-bold text-primary-foreground/70 sm:block">
                      Monitoreo continuo de sala
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>

          {/* Bottom row — next turns + thank-you card */}
          <div className={cn('grid gap-2 sm:gap-3', COL_TEMPLATE)}>
            <div className="flex flex-col gap-2 sm:gap-3 lg:col-span-2">
              <div className="flex items-center gap-3 px-1">
                <Clock3 className="h-5 w-5 text-accent sm:h-6 sm:w-6" aria-hidden />
                <h2 className="font-display text-[15px] font-black uppercase leading-none text-foreground sm:text-[18px]">
                  Siguientes turnos
                </h2>
              </div>
              {next_turns.length === 0 ? (
                <div className="flex min-h-[100px] items-center justify-center rounded-[10px] border border-border bg-muted sm:min-h-[150px]">
                  <p className="px-4 text-center text-[13px] font-bold text-muted-foreground sm:text-[14px]">
                    No hay más turnos en espera.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
                  {next_turns.map((card, index) => {
                    const species = formatPetSpecies(card.pet_species);
                    const pet_image = getPetImage(species, card.sale_order_id);
                    return (
                      <article
                        key={card.sale_order_id}
                        className="flex h-auto min-h-[110px] items-center gap-3 rounded-[10px] border border-primary bg-gradient-to-br from-primary to-primary/85 px-3 py-3 text-primary-foreground shadow-[0_6px_14px_rgba(25,77,157,0.22)] sm:h-[150px] sm:gap-4 sm:px-5 sm:py-0"
                      >
                        <p className="font-display text-[32px] font-black leading-none tabular-nums sm:text-[46px]">
                          {String(index + 2).padStart(2, '0')}
                        </p>
                        <img
                          src={pet_image}
                          alt={species}
                          className="h-[56px] w-[56px] shrink-0 rounded-full border-2 border-primary-foreground/40 object-cover sm:h-[78px] sm:w-[78px]"
                        />
                        <div className="min-w-0">
                          <p className="truncate text-[14px] font-black leading-none sm:text-[16px]">
                            {card.pet_name ?? t('display.waiting_room.no_pet')}
                          </p>
                          <TurnMeta species={species} queued_at={card.queued_at} variant="compact" />
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>

            <article className="flex items-center gap-3 rounded-[10px] border border-border bg-card px-3 py-3 shadow-[0_4px_12px_rgba(19,39,80,0.06)] sm:gap-4 sm:px-5 sm:py-4">
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary sm:h-12 sm:w-12">
                <ShieldCheck className="h-6 w-6 sm:h-7 sm:w-7" aria-hidden />
              </div>
              <p className="text-[12px] font-bold leading-snug text-muted-foreground sm:text-[13px]">
                Gracias por tu paciencia y confianza.{' '}
                <span className="text-foreground">
                  Estamos para cuidar a quienes más quieres.
                </span>
              </p>
            </article>
          </div>
        </main>
      </div>
    </div>
  );
}

function NowServingCard({
  card,
  t,
}: {
  card: MedicalQueueCard | null;
  t: (key: string) => string;
}): JSX.Element {
  const species = formatPetSpecies(card?.pet_species);
  const pet_image = getPetImage(species, card?.sale_order_id ?? 'now-serving');

  return (
    <article className="relative flex-1 overflow-hidden rounded-[13px] border border-primary bg-gradient-to-br from-primary to-primary/85 p-4 text-primary-foreground shadow-[0_8px_18px_rgba(25,77,157,0.25)] sm:p-5 2xl:p-7">
      <Stethoscope
        className="absolute -right-4 -top-4 h-28 w-28 text-primary-foreground/10 sm:h-36 sm:w-36"
        aria-hidden
      />
      <Bone
        className="absolute bottom-6 left-6 h-10 w-10 rotate-[-18deg] text-primary-foreground/10 sm:h-14 sm:w-14"
        aria-hidden
      />

      <span className="inline-block rounded-full bg-foreground/20 px-4 py-1.5 font-display text-[12px] font-black uppercase leading-none sm:text-[13px]">
        Ahora atendiendo
      </span>

      {!card ? (
        <div className="mt-8 flex flex-col items-center justify-center gap-3 text-center sm:mt-10">
          <PawPrint className="h-12 w-12 text-primary-foreground/40 sm:h-16 sm:w-16" aria-hidden />
          <p className="font-display text-[18px] font-black uppercase leading-tight sm:text-[22px]">
            Sin pacientes en fila
          </p>
        </div>
      ) : (
        <div className="relative mt-5 grid grid-cols-[auto_90px_1fr] items-center gap-3 sm:mt-6 sm:grid-cols-[auto_110px_1fr] sm:gap-5 2xl:grid-cols-[auto_130px_1fr]">
          <div>
            <p className="font-display text-[44px] font-black leading-none tabular-nums sm:text-[56px] 2xl:text-[70px]">
              01
            </p>
            <p className="font-display text-[11px] font-black uppercase tracking-wide sm:text-[13px]">
              Turno
            </p>
          </div>
          <img
            src={pet_image}
            alt={species}
            className="h-[90px] w-[90px] rounded-full border-2 border-primary-foreground/40 bg-card object-cover shadow-sm sm:h-[110px] sm:w-[110px] 2xl:h-[130px] 2xl:w-[130px]"
          />
          <div className="min-w-0">
            <p className="truncate font-display text-[19px] font-black leading-none sm:text-[23px] 2xl:text-[27px]">
              {card.pet_name ?? t('display.waiting_room.no_pet')}
            </p>
            <div className="mt-3">
              <TurnMeta species={species} queued_at={card.queued_at} variant="hero" />
            </div>
          </div>
        </div>
      )}
    </article>
  );
}
